import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Typography } from '@mui/material';
import { InputAdornment, IconButton } from '@mui/material';
import pino from 'pino';
import { useState } from 'react';
import type { ChangeEvent, FC, FocusEvent as ReactFocusEvent, FormEvent } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router';
import { useEffectOnce } from 'react-use';
import { z } from 'zod';

import { notificationStrings, sessionStrings } from '../../../../../../../constants/strings';
import { userKeys } from '../../../../../../../queries/query-keys';
import { useActivateUserQuery, useSessionLogoutQuery } from '../../../../../../../queries/sessionQuery';
import type { ActivateUserWithTemporaryPassword } from '../../../../../../../repositories/sessionRepository';
import UserRepository from '../../../../../../../repositories/userRepository';
import type { registerActivateLoader } from '../../../../../../../router/loaders/signin/registerActivateLoader';
import { queryClient } from '../../../../../../../router/Root';
import { useSigninMessageNavigation } from '../../../../../../../router/routerUtils';
import { isCompanyOrSupportAdmin, isSupportAdmin } from '../../../../../../../selectors/userSelectors';
import { useZustand } from '../../../../../../../zustandStore';
import type { PhoneDetails } from '../../../../../../../zustandStore/slices';
import PageSpinner from '../../../../../../molecules/pageSpinner';
import { passwordRegex } from '../../../../activateForm/activationForm';
import FormWrapper from '../../../formWrapper';
import { useValidation } from '../../../hooks/useValidation';
import FormInput from '../../common/formInput';

const logger = pino();
const isInvitation = !location.pathname.includes('register');

export const passwordRequireMessage = globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP
	? 'Please note that passwords must contain a minimum of ten characters, with at least one capital letter, one number and one special character.'
	: 'Please note that passwords must contain a minimum of ten characters, which must include at least one capital letter, one number and one special character.';

const passwordSchema = z
	.object({
		password: z.string().check(ctx => {
			if (ctx.value.length < 10) {
				ctx.issues.push({
					code: 'custom',
					message: passwordRequireMessage,
					input: ctx.value,
				});
			} else if (!passwordRegex.test(ctx.value)) {
				ctx.issues.push({
					code: 'custom',
					message: passwordRequireMessage,
					input: ctx.value,
				});
			}
		}),
		confirmPassword: z.string(),
	})
	.refine(data => data.password === data.confirmPassword, {
		path: ['confirmPassword'],
		message: 'Passwords must match',
	});

type PasswordSchemaType = z.infer<typeof passwordSchema>;

type MFA_STATUS = 'setup_required' | 'login_required' | 'disabled' | 'authenticated';
type PHONE_STATUS = 'verified' | 'verification_needed' | 'disabled';

type PHONE_SETUP = {
	status: PHONE_STATUS;
	phoneDetails?: PhoneDetails | null | undefined;
};

export type MFARegister = {
	mfaOtp: {
		status: MFA_STATUS;
		secret?: string; //used in HSBC for phone sms
		otpSecret?: string; //used in AMEX for authenticator app
		qrCode: string;
	};
	mfaSms: {
		status: Extract<MFA_STATUS, 'authenticated' | 'login_required'>;
		phoneDetails: PHONE_SETUP['phoneDetails'];
	};
	phoneSetup: PHONE_SETUP;
	fullyAuthenticated?: boolean;
	skipMfaAllowed?: boolean;
	passwordAttemptsExceeded?: boolean; //used in HSBC FOR LOCKED_ACCOUNT on too many wrong email/pass attempts
	activationCodeAttemptsExceeded?: boolean; //used in HSBC FOR LOCKED_ACCOUNT
	mfaCodeAttemptsExceeded?: boolean; //used in HSBC FOR LOCKED_ACCOUNT too many wrong attempts on otp code
};

const PasswordSetupForm: FC = () => {
	const initialData = useLoaderData() as Awaited<ReturnType<typeof registerActivateLoader>>;
	const { data, isLoading, error } = initialData || {};
	const [formState, setFormState] = useState<PasswordSchemaType>({
		password: '',
		confirmPassword: '',
	});
	const { validateField, clearFieldError, fieldErrors, validateAllSchema } = useValidation<PasswordSchemaType>();
	const activateUserWithTemporaryPassword = useActivateUserQuery<ActivateUserWithTemporaryPassword, MFARegister>();
	const { userId, activationToken } = useParams();
	const { steps, setPageLoading, setDialog, closeDialog, setStepsData, setCurrentStep, setNotification } = useZustand(
		state => ({
			...state,
		}),
	);
	const navigate = useNavigate();
	const logout = useSessionLogoutQuery();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const { navigateToLockedAccountMessage } = useSigninMessageNavigation();

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormState(prev => {
			const updatedState = { ...prev, [name]: value };
			clearFieldError(name as keyof PasswordSchemaType);
			return updatedState;
		});
	};

	const handleBlur = (e?: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (e) {
			const { name = '' } = e.target;
			validateField(passwordSchema, name as keyof PasswordSchemaType, formState);
		}
	};

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		validateAllSchema(passwordSchema, formState);

		if (!data) {
			logger.error('initial data is missing');
			return;
		}
		setPageLoading(true);
		const options = {
			activationToken: activationToken || '',
			companyName: data?.companyName,
			email: data?.username,
			username: data?.username,
			firstName: steps?.data?.user ? steps?.data?.user.firstName : data?.firstName,
			lastName: steps?.data?.user ? steps?.data?.user.lastName : data?.lastName,
			password: formState.password,
			userId: userId || '',
			temporaryPassword: steps.data?.['tempPassword'] || '',
		};
		activateUserWithTemporaryPassword.mutate(options, {
			onSuccess: async data => {
				if (data.phoneSetup || data.mfaOtp || data.mfaSms) {
					if (options.username) {
						setStepsData({ email: options.username });
					}

					if (data.phoneSetup) {
						setStepsData({ phoneSetup: data.phoneSetup });
					}
					if (data.mfaOtp) {
						setStepsData({
							mfaOtp: {
								status: data.mfaOtp.status,
								secret: data.mfaOtp.secret || '',
								qrCode: data.mfaOtp.qrCode,
							},
						});
					}
					if (data.mfaSms) {
						setStepsData({ mfaSms: data.mfaSms });
					}

					setCurrentStep(steps.currentStep + 1);
				} else {
					if (isInvitation) {
						setCurrentStep(steps.currentStep + 1);
						setPageLoading(false);
					}
					const user = await UserRepository.getUser();
					queryClient.setQueryData([userKeys.getUser], user);
					const isTsAdmin = isSupportAdmin(user?.roles);
					setPageLoading(false);
					if (user && !isTsAdmin && !isCompanyOrSupportAdmin(user?.roles) && user?.roles.length === 0) {
						logout.mutate();
						setFormState({ password: '', confirmPassword: '' });
						setNotification([
							{
								type: notificationStrings.ERROR,
								message: 'Your account has been disabled, please contact your company or team administrator.',
							},
						]);
					} else if (isTsAdmin) {
						void navigate('/overview');
					} else {
						if (isCompanyOrSupportAdmin(user?.roles || [])) {
							void navigate('/wallet');
						} else {
							void navigate('/wallet/my-cards');
						}
					}
				}
			},
			onError: () => {
				setDialog({
					type: notificationStrings.WARNING,
					message: 'Oh snap! We were not able to create your account.',
					open: true,
					actionText: 'TRY AGAIN',
					showCancel: false,
					onAccept: closeDialog,
				});
			},
			onSettled: () => {
				setPageLoading(false);
			},
		});
	};

	useEffectOnce(() => {
		if (!data) {
			return;
		}
		if (data.activationCodeAttemptsExceeded) {
			navigateToLockedAccountMessage('activation');
			return;
		}
	});

	useEffectOnce(() => {
		if (!error) {
			return;
		}
		void navigate('/signin/message', {
			state: {
				heading: 'Sorry, we are having technical difficulties. Please try again later.',
				message:
					'The link to activate your account is invalid or has expired. Please check with the person who invited you to get another invitation.',
			},
		});
	});

	return (
		<FormWrapper
			formName="Setup your password"
			onSubmit={handleSubmit}
			submitButton={{
				label: sessionStrings.CONTINUE,
				isDisabled: !passwordSchema.safeParse(formState).success,
			}}
			isFormPending={activateUserWithTemporaryPassword.isPending}
		>
			<Typography
				variant="body1"
				style={{
					fontStyle: 'normal',
					lineHeight: '18px',
					color: '#000000',
				}}
			>
				{passwordRequireMessage}
			</Typography>
			<FormInput
				label="Password"
				name="password"
				type={showPassword ? 'text' : 'password'}
				value={formState.password}
				error={fieldErrors['password']}
				onChange={handleInputChange}
				onBlur={handleBlur}
				endAdornment={
					formState.password.length > 0 ? (
						<InputAdornment position="end">
							<IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
								{showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
							</IconButton>
						</InputAdornment>
					) : null
				}
			/>
			<FormInput
				label={sessionStrings.CONFIRM_PASSWORD}
				name="confirmPassword"
				type={showConfirmPassword ? 'text' : 'password'}
				value={formState.confirmPassword}
				error={fieldErrors['confirmPassword']}
				onChange={handleInputChange}
				onBlur={handleBlur}
				endAdornment={
					formState.confirmPassword.length > 0 ? (
						<InputAdornment position="end">
							<IconButton onClick={() => setShowConfirmPassword(prev => !prev)} edge="end">
								{showConfirmPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
							</IconButton>
						</InputAdornment>
					) : null
				}
			/>
			{isLoading ? <PageSpinner isLoading /> : null}
		</FormWrapper>
	);
};

export default PasswordSetupForm;
